import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import VerifiedBadge from "../shared/VerifiedBadge";
import { searchSellers } from "../../api/shops";
import { shopPath } from "../../utils/shopPath";
import { imageProps, SIZES } from "../../utils/imageUrl";

/* The search page's Sellers tab (shop pages, 2026-10-17): find a shop by
   its name or what it sells. Waits 300 ms after typing and needs at least
   2 characters, so a shopper typing does not send a request per key; the
   backend limits callers too (30 a minute) and its 429 gets a plain
   sentence, not an error code. */

export const SELLER_SEARCH_DELAY_MS = 300;
export const SELLER_SEARCH_MIN = 2;

function SellerHit({ hit }) {
  const place = [hit.town, hit.county].filter(Boolean).join(", ");
  return (
    <li className="seller-hit">
      <Link to={shopPath(hit.handle)} className="seller-hit-link">
        {hit.avatar_url
          ? <img className="seller-hit-avatar" src={hit.avatar_url} alt="" />
          : <span className="seller-hit-avatar seller-hit-avatar--initial" aria-hidden="true">{(hit.display_name.replace(/^@/, "")[0] || "?").toUpperCase()}</span>}
        <span className="seller-hit-text">
          <span className="seller-hit-name">
            {hit.display_name}
            {hit.is_verified && <VerifiedBadge />}
          </span>
          <span className="seller-hit-meta">
            {[hit.is_platform ? "Official Emporia store" : null, place || null,
              `${hit.live_product_count} product${hit.live_product_count === 1 ? "" : "s"}`].filter(Boolean).join(" · ")}
          </span>
        </span>
        {hit.thumbnails?.length > 0 && (
          <span className="seller-hit-thumbs" aria-hidden="true">
            {hit.thumbnails.slice(0, 3).map((src) => <img key={src} {...imageProps(src, "product", SIZES.thumb)} alt="" loading="lazy" decoding="async" />)}
          </span>
        )}
      </Link>
    </li>
  );
}

const SellerSearch = () => {
  const [params, setParams] = useSearchParams();
  const [text, setText] = useState(params.get("q") || "");
  const [state, setState] = useState({ status: "idle", results: [], page: 1, hasMore: false, error: "" });
  const requestId = useRef(0);
  const query = text.trim();
  // setSearchParams changes identity whenever the address does, so it is
  // read through a ref: as an effect dependency, writing the address would
  // re-run the search every 300 ms and discard each answer as it arrived.
  const setParamsRef = useRef(setParams);
  setParamsRef.current = setParams;
  const paramsRef = useRef(params);
  paramsRef.current = params;

  useEffect(() => {
    if (query.length < SELLER_SEARCH_MIN) {
      setState({ status: "idle", results: [], page: 1, hasMore: false, error: "" });
      return undefined;
    }
    const timer = setTimeout(() => {
      const id = ++requestId.current;
      setState((s) => ({ ...s, status: "loading", error: "" }));
      // Keep the address shareable, without a history entry per pause.
      if (paramsRef.current.get("q") !== query || paramsRef.current.get("tab") !== "sellers") {
        const next = new URLSearchParams(paramsRef.current);
        next.set("tab", "sellers");
        next.set("q", query);
        setParamsRef.current(next, { replace: true });
      }
      searchSellers(query, 1)
        .then((r) => id === requestId.current && setState({ status: "done", results: r.results || [], page: 1, hasMore: Boolean(r.has_more), error: "" }))
        .catch((err) => id === requestId.current && setState({ status: "done", results: [], page: 1, hasMore: false,
          error: err.status === 429 ? "Please wait a moment and try again." : "We couldn't search shops just now. Please try again." }));
    }, SELLER_SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const more = () => {
    const id = requestId.current;
    const page = state.page + 1;
    setState((s) => ({ ...s, status: "loading-more" }));
    searchSellers(query, page)
      .then((r) => id === requestId.current && setState((s) => ({ ...s, status: "done", results: [...s.results, ...(r.results || [])], page, hasMore: Boolean(r.has_more) })))
      .catch((err) => id === requestId.current && setState((s) => ({ ...s, status: "done",
        error: err.status === 429 ? "Please wait a moment and try again." : "We couldn't load more shops." })));
  };

  return (
    <section className="seller-search" aria-label="Find a shop">
      <input
        type="search"
        className="seller-search-input"
        placeholder="Search shops by name or what they sell"
        aria-label="Search shops"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {query.length > 0 && query.length < SELLER_SEARCH_MIN && (
        <p className="search-count">Type at least {SELLER_SEARCH_MIN} characters.</p>
      )}
      {state.error && <p className="seller-search-error" role="alert">{state.error}</p>}
      {state.status === "loading" && <p className="search-count" aria-live="polite">Searching shops…</p>}
      {state.status === "done" && !state.error && state.results.length === 0 && (
        <div className="search-empty"><p>No shops match <strong>"{query}"</strong>.</p></div>
      )}
      {state.results.length > 0 && (
        <ul className="seller-hits">
          {state.results.map((hit) => <SellerHit key={hit.handle} hit={hit} />)}
        </ul>
      )}
      {state.hasMore && (
        <div className="seller-search-more">
          <button type="button" className="seller-search-more-btn" onClick={more} disabled={state.status === "loading-more"}>
            {state.status === "loading-more" ? "Loading…" : "More shops"}
          </button>
        </div>
      )}
    </section>
  );
};

export default SellerSearch;
